import { motion } from 'framer-motion';
import { Database, Layers, Brain, ShoppingBag, Truck, Award } from 'lucide-react';

export function Technology() {
  const platforms = [
    {
      title: '数据中台',
      description: '统一数据管理与分析',
      icon: Database,
      color: 'from-orange-600 to-yellow-400',
    },
    {
      title: '业务中台',
      description: '业务能力复用与共享',
      icon: Layers,
      color: 'from-red-600 to-orange-400',
    },
  ];

  const products = [
    {
      icon: Truck,
      title: '数字化供应链系统',
      description: '端到端供应链可视化管理',
    },
    {
      icon: Layers,
      title: '智慧物流管理平台',
      description: '全流程智能化物流解决方案',
    },
    {
      icon: Brain,
      title: 'AI数据决策中台',
      description: '智能数据分析与决策支持',
    },
    {
      icon: ShoppingBag,
      title: '全渠道电商平台',
      description: '一体化电商运营管理系统',
    },
  ];

  const certifications = [
    { icon: Award, title: '国家高新技术企业' },
    { icon: Award, title: 'ISO质量管理体系认证' },
    { icon: Award, title: '软件企业认证' },
  ];

  return (
    <section id="technology" className="py-16 sm:py-20 bg-gradient-to-br from-gray-900 via-orange-900 to-gray-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16"
        >
          <div className="inline-block px-4 py-1 rounded-full bg-white/10 backdrop-blur-md text-yellow-400 text-sm mb-4">
            技术实力
          </div>
          <h2 className="text-white mb-4 px-4">领先的技术架构</h2>
          <p className="text-gray-300 max-w-2xl mx-auto px-4">
            基于双中台架构，构建完整的技术产品矩阵
          </p>
        </motion.div>

        {/* 双中台架构 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <h3 className="text-center text-white mb-8">双中台架构</h3>
          <div className="grid md:grid-cols-2 gap-8">
            {platforms.map((platform, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="relative group"
              >
                <div className="relative p-8 rounded-2xl bg-white/5 backdrop-blur-md border border-white/10 hover:border-white/20 transition-all overflow-hidden">
                  {/* Gradient Background */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${platform.color} opacity-0 group-hover:opacity-10 transition-opacity`}></div>
                  
                  <div className="relative z-10">
                    <div className={`inline-flex w-16 h-16 rounded-xl bg-gradient-to-br ${platform.color} items-center justify-center mb-6`}>
                      <platform.icon size={28} />
                    </div>
                    <h4 className="text-white mb-3">{platform.title}</h4>
                    <p className="text-gray-400">{platform.description}</p>
                  </div>

                  {/* Decorative corner */}
                  <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${platform.color} opacity-10 blur-3xl`}></div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Connection Line */}
          <div className="flex justify-center my-8">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
              <div className="w-16 h-0.5 bg-gradient-to-r from-yellow-400 to-orange-400"></div>
              <div className="text-gray-400 px-4">协同赋能</div>
              <div className="w-16 h-0.5 bg-gradient-to-r from-orange-400 to-red-400"></div>
              <div className="w-3 h-3 rounded-full bg-red-400"></div>
            </div>
          </div>
        </motion.div>

        {/* 核心产品矩阵 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <h3 className="text-center text-white mb-8">核心产品矩阵</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 hover:border-yellow-400/50 transition-all group"
              >
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-yellow-400 to-orange-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <product.icon size={24} />
                </div>
                <h4 className="text-white mb-2">{product.title}</h4>
                <p className="text-gray-400 text-sm">{product.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* 技术认证 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <h3 className="text-white mb-8">技术认证</h3>
          <div className="flex flex-wrap justify-center gap-6">
            {certifications.map((cert, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex items-center space-x-3 px-6 py-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:border-yellow-400/50 transition-all"
              >
                <cert.icon size={20} className="text-yellow-400" />
                <span className="text-white">{cert.title}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Technology Stack */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-16 text-center"
        >
          <div className="inline-flex items-center space-x-4 px-8 py-4 rounded-full bg-white/5 backdrop-blur-md border border-white/10">
            <span className="text-gray-400">技术栈:</span>
            {['JAVA', '.NET', 'SaaS', 'AI', '大数据'].map((tech, index) => (
              <span
                key={index}
                className="px-4 py-1 rounded-full bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white text-sm"
              >
                {tech}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}